import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Box } from '@mui/material'
import ReactQuill, { Quill } from 'react-quill'
import 'react-quill/dist/quill.snow.css'

// Register style-based attributors for email-compatible inline styles
const AlignStyle = Quill.import('attributors/style/align')
const BackgroundStyle = Quill.import('attributors/style/background')
const ColorStyle = Quill.import('attributors/style/color')
const DirectionStyle = Quill.import('attributors/style/direction')
const FontStyle = Quill.import('attributors/style/font')
const SizeStyle = Quill.import('attributors/style/size')

SizeStyle.whitelist = ['10px', '12px', '18px', '24px', '32px', '48px', '64px', '72px']

Quill.register(AlignStyle, true)
Quill.register(BackgroundStyle, true)
Quill.register(ColorStyle, true)
Quill.register(DirectionStyle, true)
Quill.register(FontStyle, true)
Quill.register(SizeStyle, true)

const RichTextEditor = ({ value, onChange, placeholder = 'Enter your message...' }) => {
  const modules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ font: [] }],
        [{ size: ['10px', '12px', false, '18px', '24px', '32px', '48px', '64px', '72px'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        ['link'],
        ['clean'],
      ],
    }),
    []
  )

  const formats = [
    'header',
    'font',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'color',
    'background',
    'align',
    'list',
    'bullet',
    'indent',
    'link',
  ]

  return (
    <Box
      sx={{
        '& .quill': {
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '4px',
          bgcolor: 'background.paper',
        },
        '& .ql-toolbar': {
          borderTopLeftRadius: '4px',
          borderTopRightRadius: '4px',
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.default',
        },
        '& .ql-container': {
          borderBottomLeftRadius: '4px',
          borderBottomRightRadius: '4px',
          fontSize: '14px',
          fontFamily: 'inherit',
        },
        '& .ql-editor': {
          minHeight: '150px',
          maxHeight: '400px',
          overflow: 'auto',
          lineHeight: 1.2,
        },
        '& .ql-editor.ql-blank::before': {
          color: 'text.disabled',
          fontStyle: 'normal',
        },
        // Size picker label styles
        '& .ql-snow .ql-picker.ql-size .ql-picker-label::before': { content: '"14px"' },
        '& .ql-snow .ql-picker.ql-size .ql-picker-item::before': { content: '"14px"' },
        '& .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="10px"]::before': { content: '"10px"' },
        '& .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="10px"]::before': { content: '"10px"' },
        '& .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="12px"]::before': { content: '"12px"' },
        '& .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="12px"]::before': { content: '"12px"' },
        '& .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="18px"]::before': { content: '"18px"' },
        '& .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="18px"]::before': { content: '"18px"' },
        '& .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="24px"]::before': { content: '"24px"' },
        '& .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="24px"]::before': { content: '"24px"' },
        '& .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="32px"]::before': { content: '"32px"' },
        '& .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="32px"]::before': { content: '"32px"' },
        '& .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="48px"]::before': { content: '"48px"' },
        '& .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="48px"]::before': { content: '"48px"' },
        '& .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="64px"]::before': { content: '"64px"' },
        '& .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="64px"]::before': { content: '"64px"' },
        '& .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="72px"]::before': { content: '"72px"' },
        '& .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="72px"]::before': { content: '"72px"' },
      }}
    >
      <ReactQuill theme="snow" value={value} onChange={onChange} modules={modules} formats={formats} placeholder={placeholder} />
    </Box>
  )
}

RichTextEditor.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
}

export default RichTextEditor
